import type { PrintReceiptSource } from '../types'

export interface PrintIdentifiers {
  TempId: string
  UniqueId: string
  Uuid: string
}

/**
 * The print service blocks duplicate jobs by `UniqueId` and rejects a blank one
 * with a 400, so these are minted per print job rather than read from the
 * record. `now` is injected so tests are deterministic.
 */
export const deriveIdentifiers = (
  source: PrintReceiptSource,
  now: number = new Date().valueOf(),
): PrintIdentifiers => {
  const stamp = now.toString()
  return {
    TempId: `${stamp}_${source?.OrderId}`,
    UniqueId: `${stamp}_${source?.UniqueId}`,
    Uuid: `${stamp}_${source?.UniqueId}`,
  }
}

/**
 * The original and its copy are built back-to-back, so their ms-timestamp ids
 * can collide and the copy would be eaten as a duplicate. Suffix them.
 */
export const withCopySuffix = (ids: PrintIdentifiers): PrintIdentifiers => ({
  TempId: `${ids.TempId}_C`,
  UniqueId: `${ids.UniqueId}_C`,
  Uuid: `${ids.Uuid}_C`,
})

/**
 * Split-bill title: when the caller stamped `SplitBillNo` (a specific bill on a
 * multi-bill table, not the "All" view), append it -> "Table A5-2". Otherwise
 * the plain table name. `Table` itself is untouched, so reports are unaffected.
 */
export const deriveReference = (source: PrintReceiptSource): string =>
  `Table ${source?.SplitBillNo ? `${source.Table}-${source.SplitBillNo}` : source?.Table}`

/** YYYYMMXXXX -> YYYYMM-XXXX. Only meaningful on the payment path. */
export const deriveReceiptId = (source: PrintReceiptSource): string | undefined => {
  if (typeof source?.OrderId !== 'number') return undefined
  const orderIdString = source.OrderId.toString()
  return `${orderIdString.slice(0, 6)}-${orderIdString.slice(6)}`
}
